<template>
  <div class="edit-amount-popup ">
    <popup :title="popupTitle"
           :buttons="buttons"
           :close-action="cancelEdit">
      <template v-slot:content>
        <div class="content-container">
          <radio-button-card title="צפי אוטומטי לפי העבר"
                               :selected="heuristicPredictionSelected"
                             @click="handleHeuristicPredictionSelected">
            <div class="ri-small-display margin-right-xl margin-top-xs history-average">
              <span>{{lastThreeMonthsAverageFormatted}}</span>
              <span v-if="lastThreeMonthsAverage" class="ri-bold-label">₪</span>
            </div>
            <div v-if="lastThreeMonthsAverage"  :class="{'bar-graph-disabled': !heuristicPredictionSelected }">
              <bar-graph :with-sign='false' :graph-height="102" :bar-values="graphData" />
            </div>
            <base-tool-tip icon-path="/images/info-icon-blue.svg" class="tooltip margin-top-s" v-else>
              <div class="ri-label">
              <div>הצפי האוטומטי יהיה זמין כשיעברו</div>
              <div>3 חודשים מרגע פתיחת הקטגוריה</div>
              </div>
            </base-tool-tip>
          </radio-button-card>
          <radio-button-card class="constant-option margin-top-m"
                             title="צפי שלי" :selected="constantPredictionSelected" @click="selectedType=predictionTypes.CONSTANT">
            <div class="input-container" :class="{'disabled-input': !constantPredictionSelected}">
              <default-text-input
                min="0"
                :value="updatedAmount"
                class="amount-input margin-bottom-xs"
                @input="onInput"
                inputType="tel"
                style="width: 200px" />
              <div class="nis ri-bold-label"
              :class="{disabled: !constantPredictionSelected}">₪</div>
            </div>
            <div v-if="errorMessage" class="error-message ri-small-body margin-bottom-s">{{errorMessage}}</div>
            <div class="ri-bold-body" :class="{selected: constantPredictionSelected}" v-if="allowedConstantInput">
              {{remainingAmountText}} ₪
            </div>
            <check-box class="margin-top-ml"
              :initially-checked="isOneTimeUpdate" v-model="isOneTimeUpdate" >
              <template v-slot:title>
                <div class="ri-label margin-right-s">שמור את הצפי הזה רק ל{{ monthFullName }}</div>
              </template>
            </check-box>
          </radio-button-card>
        </div>
      </template>
    </popup>
  </div>
</template>

<script>
import moment from 'moment';
import _ from 'lodash';
import BaseUI from '@riseupil/base-ui';
import { mapGetters, mapActions, mapState } from 'vuex';
import EventBus from '@/event-bus/event-bus';
import EditCategoryAmountToaster
  from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/toasters/EditCategoryAmountToaster';
import trackingCategoryApi from '@/api/TrackingCategoriesApi';
import RadioButtonCard
  // eslint-disable-next-line max-len
  from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/cashflow-view/current-month/components/cashflow-edit/RadioButtonCard.vue';
import {
  getEnvelopeTrendValues, getTcTrendValuesForCashflow,
} from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/cashflow-view/cashflow-trend/cashflow-trends';
import moneyUtils from '@/utils/money';
import BaseToolTip from '@/base-components/onboarding/BaseToolTip';
import Segment from '../../../../../../../../../Segment';

const PREDICTION_TYPES = {
  HEURISTIC: 'heuristic',
  CONSTANT: 'constant',
};

export default {
  name: 'EditMonthlyCategoryAmount',
  components: {
    BaseToolTip,
    RadioButtonCard,
    DefaultTextInput: BaseUI.DefaultTextInput,
    Popup: BaseUI.Popup,
    CheckBox: BaseUI.CheckBox,
    BarGraph: BaseUI.BarGraph,
  },
  data() {
    return {
      updatedAmount: null,
      displayErrorMessage: false,
      isOneTimeUpdate: false,
      selectedType: null,
      buttons: [],
      isLoading: false,
    };
  },
  created() {
    this.updatedAmount = _.ceil(this.originalAmount);
  },
  mounted() {
    this.selectedType = this.trackingCategory.categoryType;
    this.initButtons();
  },
  props: {
    expenseCategory: {
      type: String,
      required: true,
    },
    category: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters('cashflowView', ['monthFullName', 'isPresentingCurrentMonth', 'trackingCategories', 'monthsAhead', 'currentBudgetDate']),
    ...mapState('cashflowView', ['presentedBudgetDate', 'allCashflows']),
    popupTitle() {
      return `כמה כסף צפוי לצאת על ${this.expenseCategory} ב${this.monthFullName}?`;
    },
    trackingCategory() {
      return this.trackingCategories.find(tc => tc.categoryId === this.category.categoryId);
    },
    graphData() {
      return getEnvelopeTrendValues(
        this.allCashflows,
        this.presentedBudgetDate,
        (cf, index) => getTcTrendValuesForCashflow(cf, index, this.trackingCategory, this.isPresentingCurrentMonth, []),
      );
    },
    lastThreeMonthsAverage() {
      const lastThreeMonthsEnvelopes = _.chain(this.allCashflows)
        .filter(c => c.budgetDate < this.presentedBudgetDate)
        .orderBy(c => c.budgetDate, 'desc')
        .take(3)
        .flatMap(c => c.envelopes)
        .filter(e => e.details && e.details.trackingCategory?._id === this.category.categoryId)
        .value();
      if (lastThreeMonthsEnvelopes.length < 3) {
        return undefined;
      }

      return _.chain(lastThreeMonthsEnvelopes)
        .map(envelope => _.sumBy(envelope.actuals, a => a.billingAmount))
        .mean()
        .round()
        .value();
    },
    lastThreeMonthsAverageFormatted() {
      if (!this.lastThreeMonthsAverage) {
        return '-';
      }

      return moneyUtils.formatAmountWithFraction(this.lastThreeMonthsAverage, 0);
    },
    predictionTypes() {
      return PREDICTION_TYPES;
    },
    heuristicPredictionSelected() {
      return this.selectedType === PREDICTION_TYPES.HEURISTIC;
    },
    constantPredictionSelected() {
      return this.selectedType === PREDICTION_TYPES.CONSTANT;
    },
    originalAmount() {
      return this.category.recommendedAmount;
    },
    envelopeId() {
      return this.category.envelopeId;
    },
    actualsSum() {
      return _.round(this.category.actualsSum, 1);
    },
    errorMessage() {
      return _.isNil(this.updatedAmount) || this.updatedAmount < 0 ? this.ERROR_MESSAGES.AMOUNT_IS_ZERO : null;
    },
    ERROR_MESSAGES() {
      return {
        LESS_THAN_ACTUAL_SUM: `הסכום הצפוי צריך להיות מעל ${this.actualsSum} ₪ שכבר יצאו על ${this.expenseCategory} החודש `,
        AMOUNT_IS_ZERO: 'הסכום הצפוי צריך להיות לפחות 0 ₪',
      };
    },
    remainingAmount() {
      return +this.updatedAmount - this.category.actualsSum;
    },
    allowedConstantInput() {
      return !_.isNil(this.remainingAmount) && !_.isNaN(this.remainingAmount) && +this.updatedAmount >= 0;
    },
    remainingAmountText() {
      const text = this.remainingAmount >= 0 ? 'נשאר להוציא' : 'חריגה של';
      return `${text} ${moneyUtils.formatAmountWithFraction(Math.abs(this.remainingAmount, 0))}`;
    },
    categoryId() {
      return this.category.categoryId;
    },
    nextMonthEnvelopeData() {
      if (!this.isPresentingCurrentMonth) {
        return null;
      }
      const budgetDate = moment(this.presentedBudgetDate, 'YYYY-MM').add(1, 'month').format('YYYY-MM');
      return {
        envelopeId: this.envelopeId.replace(/^\d{4}-\d{2}/, budgetDate),
        budgetDate,
      };
    },
    currentMonthEnvelopeData() {
      if (this.isPresentingCurrentMonth) {
        return null;
      }
      const currentBudget = this.allCashflows.find(cf => cf.budgetDate === this.currentBudgetDate);
      if (!currentBudget) {
        return null;
      }
      const envelope = currentBudget.envelopes.find(
        e => e.details?.trackingCategory?._id === this.categoryId,
      );
      if (!envelope) {
        return null;
      }
      return {
        envelopeId: envelope.id,
        amount: envelope.predictedAmount ?? envelope.originalAmount,
      };
    },
  },
  methods: {
    ...mapActions('editCashflow', ['updateEnvelopeAmount']),
    ...mapActions('modalRootStore', ['closeModal']),
    handleHeuristicPredictionSelected() {
      if (this.lastThreeMonthsAverage) {
        this.selectedType = PREDICTION_TYPES.HEURISTIC;
      }
    },
    initButtons() {
      this.buttons = [{
        title: 'עדכון',
        size: 'slim',
        action: this.updateEnvelopeAmountApprove,
        disabled: !this.allowedConstantInput && this.constantPredictionSelected,
        loading: this.isLoading,
      }];
    },
    async updateEnvelopeAmountApprove() {
      const amount = +(this.heuristicPredictionSelected ? this.lastThreeMonthsAverage : this.updatedAmount);
      if (this.constantPredictionSelected && this.errorMessage) {
        Segment.trackUserInteraction('EditMonthlyAmountErrorShown', {
          expenseCategory: this.expenseCategory,
          envelopeId: this.envelopeId,
          oldAmount: this.originalAmount,
          newAmount: this.updatedAmount,
        });
        this.displayErrorMessage = true;
        return;
      }

      this.isLoading = true;
      this.initButtons();
      Segment.trackUserInteraction('EditAmountApprovedClicked', {
        expenseCategory: this.expenseCategory,
        envelopeId: this.envelopeId,
        oldAmount: this.originalAmount,
        newAmount: amount,
        predictionType: this.selectedType,
      });

      if (this.heuristicPredictionSelected) {
        await trackingCategoryApi.updateTrackingCategoryHeuristicPrediction(this.categoryId);
        this.updateEnvelopeAmount({
          envelopeId: this.envelopeId,
          amount,
        });
        if (this.currentMonthEnvelopeData) {
          this.updateEnvelopeAmount({
            envelopeId: this.currentMonthEnvelopeData.envelopeId,
            amount: this.currentMonthEnvelopeData.amount,
            applyOnBudgetDate: this.currentBudgetDate,
          });
        }
        if (this.nextMonthEnvelopeData) {
          this.updateEnvelopeAmount({
            envelopeId: this.nextMonthEnvelopeData.envelopeId,
            amount,
            applyOnBudgetDate: this.nextMonthEnvelopeData.budgetDate,
          });
        }
      } else {
        if (!this.isOneTimeUpdate) {
          await trackingCategoryApi.updateTrackingCategoryConstantPrediction({
            categoryId: this.category.categoryId,
            amount,
          });
        }

        this.updateEnvelopeAmount({
          envelopeId: this.envelopeId,
          amount,
        });
        if (!this.isOneTimeUpdate && this.currentMonthEnvelopeData) {
          this.updateEnvelopeAmount({
            envelopeId: this.currentMonthEnvelopeData.envelopeId,
            amount: this.currentMonthEnvelopeData.amount,
            applyOnBudgetDate: this.currentBudgetDate,
          });
        }
        if (!this.isOneTimeUpdate && this.nextMonthEnvelopeData) {
          this.updateEnvelopeAmount({
            envelopeId: this.nextMonthEnvelopeData.envelopeId,
            amount,
            applyOnBudgetDate: this.nextMonthEnvelopeData.budgetDate,
          });
        }
      }

      EventBus.$emit('open-toaster', {
        component: EditCategoryAmountToaster,
        props: {
          categoryName: this.expenseCategory,
          timeFrameTitle: this.timeFrameTitle,
        },
      });
      this.$emit('success');
      this.closeModal();
    },
    onInput(value) {
      this.displayErrorMessage = false;
      this.updatedAmount = value;
      this.initButtons();
    },
    cancelEdit() {
      Segment.trackUserInteraction('EditMonthlyAmountCanceled', {
        expenseCategory: this.expenseCategory,
        envelopeId: this.envelopeId,
      });
      this.closeModal();
    },
  },
  watch: {
    // Re-triggering DOM change in case of error message because "Popup" component receives it buttons as array, so when the inner property of
    // array item change, The DOM does not automatically change.
    errorMessage() {
      this.initButtons();
    },
    selectedType() {
      this.initButtons();
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '~@riseupil/base-ui/src/scss/riseup-colors';
  @import '~@riseupil/base-ui/src/scss/typography';
  @import '~@riseupil/base-ui/src/scss/border-radius';

  .edit-amount-popup {
    .content-container {
      padding-bottom: 13px;

      .error-message {
        color: $riseup_red;
      }

      ::v-deep .bar-graph .month-names .month-name span  {
        color: $riseup_black;
      }

      .bar-graph-disabled {
        ::v-deep .bar-graph .month-names .month-name span  {
          color: $riseup_gray_20;
        }
      }

      .constant-option{
        .input-container{
          display: flex;
          flex-direction: row;
          align-items: center;

          .nis {
            height: 100%;
            margin-right: 20px;
            margin-top: 20px;
            font-size: 18px;
            color: $riseup_blue;
          }

          .disabled {
            color: $riseup_gray_20;
          }
        }

        ::v-deep .base-input .input-container input.normal{
          font-size: 40px;
          font-weight: bold;
          padding-top: 8px;
          padding-bottom: 4px;
        }

        .disabled-input {
          ::v-deep .base-input .input-container input.normal{
            color: $riseup_gray_20
          }
        }

        &.selected {
          color: $riseup_black;
        }
      }
    }

    .tooltip {
      color: $riseup_black;
      padding: 12px;
      padding-left: 24px;
      align-items: center;
    }
  }

</style>
